import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MatrixClient } from 'matrix-js-sdk';
import { disconnectMatrixSession, getClientInstanceId, getOrCreateMatrixClient } from '../matrixClient';
import MatrixContext, { IMatrixContextValue, TMatrixConnectionStatus } from './MatrixContext';
import { idpService } from 'services/api/identity-provider.service';
import { isMatrixEnabled } from 'kits/matrixFeatureFlag';

export interface IMatrixProviderProps {
    children?: React.ReactNode;
}

const MatrixProvider: React.FC<IMatrixProviderProps> = ({ children }) => {
    const [client, setClient] = useState<MatrixClient | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<TMatrixConnectionStatus>('connecting');
    const isMountedRef = useRef(true);

    const connect = useCallback(async () => {
        // Matrix chat is disabled on production until the feature is complete. Bailing out here keeps
        // the whole stack dormant: no matrix session is created server-side, no rust-crypto WASM is
        // fetched and no /sync runs. MatrixAdapter no-ops on its own since every effect guards on
        // `client` being set.
        if (!isMatrixEnabled()) {
            setConnectionStatus('unavailable');
            return;
        }

        const token = localStorage.getItem('ivAccessToken');
        if (!token) {
            setConnectionStatus('unavailable');
            return;
        }

        // Distinguish the very first connect (still shows the plain "connecting" spinner) from
        // an explicit Reconnect after a Disconnect, which flips to the dedicated "reconnecting"
        // status instead of silently reusing the initial-connect one.
        setConnectionStatus((previousStatus) => (previousStatus === 'disconnected' ? 'reconnecting' : 'connecting'));

        try {
            // Reuses the cached client across remounts (Core, and MatrixProvider with it,
            // remounts on every in-app navigation) instead of creating a new Matrix
            // session/device and reconnecting on every navigation.
            const matrixClient = await getOrCreateMatrixClient((clientInstanceId, forceNewDevice) =>
                idpService.getMatrixSession({ clientInstanceId, forceNewDevice })
            );
            if (!isMountedRef.current) return;
            setClient(matrixClient);
            setConnectionStatus('connected');
        } catch (error) {
            console.error('Matrix client failed to connect', error);
            if (isMountedRef.current) setConnectionStatus('unavailable');
        }
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        void connect();

        return () => {
            isMountedRef.current = false;
        };
    }, [connect]);

    const disconnect = useCallback(async () => {
        if (!client) return;

        const clientInstanceId = getClientInstanceId();
        if (!clientInstanceId) return; // no persisted id means no real device to disconnect

        setClient(null);
        setConnectionStatus('disconnected');

        try {
            await disconnectMatrixSession(client, () => idpService.destroyMatrixSession({ clientInstanceId }));
        } catch (error) {
            console.error('Failed to destroy matrix session', error);
        }
    }, [client]);

    const reconnect = useCallback(() => connect(), [connect]);

    const value: IMatrixContextValue = { client, connectionStatus, disconnect, reconnect };

    return <MatrixContext.Provider value={value}>{children}</MatrixContext.Provider>;
};

export default MatrixProvider;
